'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SimpleInterestCalculator() {
  const t = useTranslations("tools.simpleInterestCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [currentMode, setCurrentMode] = useState<'balance' | 'principal' | 'rate' | 'term'>('balance');
  const [inputs, setInputs] = useState({
    principal: 20000,
    rate: 3,
    term: 10,
    balance: 26000,
    ratePeriod: 'year' as 'year' | 'month',
    termPeriod: 'years' as 'years' | 'months'
  });
  const [results, setResults] = useState<{
    endBalance: number;
    totalInterest: number;
    steps: string[];
    principal?: number;
    rate?: number;
    term?: number;
  } | null>(null);

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    accentHover: '#d97706',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#166534',
    successBg: '#f0fdf4',
    successBorder: '#86efac',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', padding: '1rem', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      { marginBottom: '1.5rem' },
      { marginBottom: '2rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
      { maxWidth: '100%' },
      { maxWidth: '900px' },
      { maxWidth: '1000px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.6rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorContent: getResponsiveStyle(
      { display: 'grid', gap: '2rem' },
      { gridTemplateColumns: '1fr', padding: '1.5rem' },
      { gridTemplateColumns: '1fr', padding: '2rem' },
      { gridTemplateColumns: '1fr 1fr', padding: '2rem' }
    ),
    calculatorForm: {
      background: '#f8fafc',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      border: '2px solid #e2e8f0'
    },
    tabMenu: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const },
      { display: 'grid', gridTemplateColumns: '1fr 1fr' },
      { display: 'flex' },
      { display: 'flex' }
    ),
    tabBtn: getResponsiveStyle(
      {
        flex: 1,
        minWidth: '100px',
        background: 'white',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#64748b',
        transition: 'all 0.3s ease'
      },
      { padding: '0.75rem 0.5rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.75rem 1rem', fontSize: '1rem' }
    ),
    tabBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: 'transparent'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#334155' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      background: 'white'
    },
    inputWithSelect: getResponsiveStyle(
      { display: 'grid', gap: '0.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr auto' },
      { gridTemplateColumns: '1fr auto' }
    ),
    selectInline: {
      minWidth: '120px'
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      border: '2px solid #86efac'
    },
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: colors.success, textAlign: 'center' as const },
      { fontSize: '1.2rem' },
      { fontSize: '1.3rem' },
      { fontSize: '1.4rem' }
    ),
    resultMain: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem 0',
      borderBottom: '1px solid #e2e8f0'
    },
    resultLabel: getResponsiveStyle(
      { fontWeight: 600, color: '#475569' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: colors.success },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' },
      { fontSize: '1.2rem' }
    ),
    calculationSteps: {
      background: 'white',
      padding: '1.25rem',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    stepsTitle: {
      margin: '0 0 1rem 0',
      color: '#334155',
      fontSize: '1rem'
    },
    step: getResponsiveStyle(
      { padding: '0.5rem 0', color: '#64748b', fontFamily: "'Courier New', monospace" },
      { fontSize: '0.8rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' }
    ),
    stepFormula: {
      color: colors.primary,
      fontWeight: 600
    },
    chartContainer: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      border: '2px solid #e2e8f0'
    },
    chartTitle: {
      textAlign: 'center' as const,
      fontWeight: 700,
      color: '#334155',
      marginBottom: '1rem',
      fontSize: '1.1rem'
    },
    barChart: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-around',
      height: '200px',
      gap: '0.5rem',
      padding: '1rem 0'
    },
    barGroup: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: '0.5rem'
    },
    bar: {
      width: '100%',
      maxWidth: '60px',
      background: 'linear-gradient(to top, #2563eb 0%, #7c3aed 100%)',
      borderRadius: '0.25rem 0.25rem 0 0',
      position: 'relative' as const,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    barLabel: {
      fontSize: '0.75rem',
      color: '#64748b',
      fontWeight: 600
    },
    pieChart: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem',
      flexWrap: 'wrap' as const,
      gap: '1rem'
    },
    pieLegend: getResponsiveStyle(
      {},
      { marginLeft: 0, marginTop: '1rem' },
      { marginLeft: '2rem', marginTop: 0 },
      { marginLeft: '2rem', marginTop: 0 }
    ),
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.75rem'
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '0.25rem'
    },
    legendText: {
      fontSize: '0.9rem',
      color: '#475569'
    },
    legendValue: {
      fontWeight: 700,
      color: '#334155'
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: 0, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.5rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    code: {
      background: '#f1f5f9',
      padding: '0.2rem 0.4rem',
      borderRadius: '0.25rem',
      fontFamily: "'Courier New', monospace",
      fontSize: '0.9em',
      color: colors.primary
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    refList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    refItem: {
      marginBottom: '0.75rem',
      paddingLeft: '1.5rem',
      position: 'relative' as const
    },
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let principal = inputs.principal;
    let rate = inputs.rate;
    let term = inputs.term;
    let balance = inputs.balance;

    const ratePerYear = inputs.ratePeriod === 'month' ? rate * 12 : rate;
    let termInYears = inputs.termPeriod === 'months' ? term / 12 : term;

    let interest: number;
    let endBalance: number;
    let steps: string[] = [];
    let calculatedPrincipal: number | undefined;
    let calculatedRate: number | undefined;
    let calculatedTerm: number | undefined;

    if (currentMode === 'balance') {
      interest = principal * (ratePerYear / 100) * termInYears;
      endBalance = principal + interest;

      steps = [
        `${t('stepInterest')} = $${principal.toLocaleString()} × ${ratePerYear}% × ${termInYears}`,
        `= $${interest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        `${t('stepEndBalance')} = $${principal.toLocaleString()} + $${interest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        `= $${endBalance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      ];
    } else if (currentMode === 'principal') {
      calculatedPrincipal = balance / (1 + (ratePerYear / 100) * termInYears);
      principal = calculatedPrincipal;
      endBalance = balance;
      interest = endBalance - principal;

      steps = [
        `${t('stepPrincipal')} = $${balance.toLocaleString()} / (1 + ${ratePerYear}% × ${termInYears})`,
        `= $${principal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        `${t('stepInterest')} = $${endBalance.toLocaleString()} - $${principal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        `= $${interest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      ];
    } else if (currentMode === 'rate') {
      interest = balance - principal;
      calculatedRate = (interest / (principal * termInYears)) * 100;
      endBalance = balance;

      steps = [
        `${t('stepRate')} = ($${balance.toLocaleString()} - $${principal.toLocaleString()}) / ($${principal.toLocaleString()} × ${termInYears}) × 100`,
        `= ${calculatedRate.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}% ${t('perYear')}`,
        `${t('stepInterest')} = $${interest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      ];
    } else if (currentMode === 'term') {
      interest = balance - principal;
      calculatedTerm = interest / (principal * (ratePerYear / 100));
      termInYears = calculatedTerm;
      endBalance = balance;

      steps = [
        `${t('stepTerm')} = ($${balance.toLocaleString()} - $${principal.toLocaleString()}) / ($${principal.toLocaleString()} × ${ratePerYear}%)`,
        `= ${termInYears.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ${t('years')}`,
        `${t('stepInterest')} = $${interest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      ];
    }

    setResults({
      endBalance: endBalance!,
      totalInterest: interest!,
      steps,
      principal: calculatedPrincipal,
      rate: calculatedRate,
      term: calculatedTerm
    });
  };

  const createBarChartData = () => {
    if (!results || currentMode !== 'balance') return [];

    const principal = inputs.principal;
    const ratePerYear = inputs.ratePeriod === 'month' ? inputs.rate * 12 : inputs.rate;
    const termInYears = inputs.termPeriod === 'months' ? inputs.term / 12 : inputs.term;

    const steps = Math.min(Math.ceil(termInYears), 10);
    const interval = termInYears / steps;
    const maxBalance = principal * (1 + (ratePerYear / 100) * termInYears);

    const data = [];
    for (let i = 0; i <= steps; i++) {
      const currentYear = interval * i;
      const currentInterest = principal * (ratePerYear / 100) * currentYear;
      const currentBalance = principal + currentInterest;
      const heightPercent = (currentBalance / maxBalance) * 100;

      data.push({
        year: currentYear,
        balance: currentBalance,
        heightPercent
      });
    }

    return data;
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const barChartData = createBarChartData();

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.calculatorContent}>
            <div>
              <div style={styles.calculatorForm}>
                <div style={styles.tabMenu}>
                  <button
                    type="button"
                    style={{...styles.tabBtn, ...(currentMode === 'balance' ? styles.tabBtnActive : {})}}
                    onClick={() => setCurrentMode('balance')}
                  >
                    {t('tabBalance')}
                  </button>
                  <button
                    type="button"
                    style={{...styles.tabBtn, ...(currentMode === 'principal' ? styles.tabBtnActive : {})}}
                    onClick={() => setCurrentMode('principal')}
                  >
                    {t('tabPrincipal')}
                  </button>
                  <button
                    type="button"
                    style={{...styles.tabBtn, ...(currentMode === 'rate' ? styles.tabBtnActive : {})}}
                    onClick={() => setCurrentMode('rate')}
                  >
                    {t('tabRate')}
                  </button>
                  <button
                    type="button"
                    style={{...styles.tabBtn, ...(currentMode === 'term' ? styles.tabBtnActive : {})}}
                    onClick={() => setCurrentMode('term')}
                  >
                    {t('tabTerm')}
                  </button>
                </div>

                <form onSubmit={handleCalculate}>
                  {currentMode !== 'principal' && (
                    <div style={styles.formGroup}>
                      <label htmlFor="principal" style={styles.label}>{t('labelPrincipal')}</label>
                      <input
                        type="number"
                        id="principal"
                        step="0.01"
                        min="0"
                        value={inputs.principal}
                        onChange={(e) => setInputs({...inputs, principal: parseFloat(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                  )}

                  {currentMode !== 'rate' && (
                    <div style={styles.formGroup}>
                      <label htmlFor="rate" style={styles.label}>{t('labelRate')}</label>
                      <div style={styles.inputWithSelect}>
                        <input
                          type="number"
                          id="rate"
                          step="0.01"
                          min="0"
                          value={inputs.rate}
                          onChange={(e) => setInputs({...inputs, rate: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          id="rate-period"
                          value={inputs.ratePeriod}
                          onChange={(e) => setInputs({...inputs, ratePeriod: e.target.value as 'year' | 'month'})}
                          style={{...styles.select, ...styles.selectInline}}
                        >
                          <option value="year">{t('perYear')}</option>
                          <option value="month">{t('perMonth')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {currentMode !== 'term' && (
                    <div style={styles.formGroup}>
                      <label htmlFor="term" style={styles.label}>{t('labelTerm')}</label>
                      <div style={styles.inputWithSelect}>
                        <input
                          type="number"
                          id="term"
                          step="0.01"
                          min="0"
                          value={inputs.term}
                          onChange={(e) => setInputs({...inputs, term: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          id="term-period"
                          value={inputs.termPeriod}
                          onChange={(e) => setInputs({...inputs, termPeriod: e.target.value as 'years' | 'months'})}
                          style={{...styles.select, ...styles.selectInline}}
                        >
                          <option value="years">{t('years')}</option>
                          <option value="months">{t('months')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {currentMode !== 'balance' && (
                    <div style={styles.formGroup}>
                      <label htmlFor="balance" style={styles.label}>{t('labelEndBalance')}</label>
                      <input
                        type="number"
                        id="balance"
                        step="0.01"
                        min="0"
                        value={inputs.balance}
                        onChange={(e) => setInputs({...inputs, balance: parseFloat(e.target.value) || 0})}
                        style={styles.input}
                      />
                    </div>
                  )}

                  <button type="submit" style={styles.calculateBtn}>
                    {t('calculateButton')}
                  </button>
                </form>
              </div>

              {results && currentMode === 'balance' && barChartData.length > 0 && (
                <div style={styles.chartContainer}>
                  <div style={styles.chartTitle}>{t('chartBarTitle')}</div>
                  <div style={styles.barChart}>
                    {barChartData.map((data, index) => (
                      <div key={index} style={styles.barGroup}>
                        <div
                          style={{...styles.bar, height: `${data.heightPercent}%`}}
                          title={`${t('year')} ${data.year.toFixed(1)}: $${data.balance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                        />
                        <div style={styles.barLabel}>{data.year.toFixed(0)}y</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              {results && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

                  <div style={styles.resultMain}>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('resultEndBalance')}</span>
                      <span style={styles.resultValue}>
                        ${results.endBalance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                    <div style={{...styles.resultItem, borderBottom: 'none'}}>
                      <span style={styles.resultLabel}>{t('resultTotalInterest')}</span>
                      <span style={styles.resultValue}>
                        ${results.totalInterest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>

                  <div style={styles.calculationSteps}>
                    <h4 style={styles.stepsTitle}>{t('stepsTitle')}</h4>
                    {results.steps.map((step, index) => (
                      <div key={index} style={styles.step}>
                        <span dangerouslySetInnerHTML={{__html: step.replace(/(\$[\d,]+\.?\d*|[\d.]+%|[\d.]+ years?)/g, '<span style="color: #2563eb; font-weight: 600;">$1</span>')}} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results && currentMode === 'balance' && (
                <div style={styles.chartContainer}>
                  <div style={styles.chartTitle}>{t('chartPieTitle')}</div>
                  <div style={styles.pieChart}>
                    <svg width="150" height="150">
                      <path
                        d={describeArc(75, 75, 60, 0, (inputs.principal / results.endBalance) * 360)}
                        fill="#2563eb"
                      />
                      <path
                        d={describeArc(75, 75, 60, (inputs.principal / results.endBalance) * 360, 360)}
                        fill="#8bbc21"
                      />
                    </svg>
                    <div style={styles.pieLegend}>
                      <div style={styles.legendItem}>
                        <div style={{...styles.legendColor, background: '#2563eb'}} />
                        <div>
                          <div style={styles.legendText}>{t('legendPrincipal')}</div>
                          <div style={styles.legendValue}>
                            ${inputs.principal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ({((inputs.principal / results.endBalance) * 100).toFixed(1)}%)
                          </div>
                        </div>
                      </div>
                      <div style={styles.legendItem}>
                        <div style={{...styles.legendColor, background: '#8bbc21'}} />
                        <div>
                          <div style={styles.legendText}>{t('legendInterest')}</div>
                          <div style={styles.legendValue}>
                            ${results.totalInterest.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ({((results.totalInterest / results.endBalance) * 100).toFixed(1)}%)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('howToStep1Title')}</strong> {t('howToStep1')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('howToStep2Title')}</strong> {t('howToStep2')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('howToStep3Title')}</strong> {t('howToStep3')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('howToStep4Title')}</strong> {t('howToStep4')}</li>
        </ol>
      </section>

      {/* Formula Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('formulaTitle')}</h2>
        <p style={styles.paragraph}>{t('formulaPara1')}</p>
        <div style={{background: '#f1f5f9', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem'}}>
          <p style={{...styles.paragraph, marginBottom: '0.5rem', fontFamily: "'Courier New', monospace"}}>
            <strong>I = P × r × t</strong>
          </p>
          <p style={{...styles.paragraph, marginBottom: 0, fontSize: '0.9rem'}}>
            {t('formulaWhere')}
          </p>
        </div>
        <p style={styles.paragraph}>{t('formulaPara2')}</p>
        <div style={{background: '#f1f5f9', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem'}}>
          <p style={{...styles.paragraph, marginBottom: 0, fontFamily: "'Courier New', monospace"}}>
            <strong>A = P(1 + rt)</strong>
          </p>
        </div>
        <h3 style={styles.h3}>{t('exampleTitle')}</h3>
        <p style={styles.paragraph}>{t('examplePara')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('exampleCalc1')}</li>
          <li style={styles.listItem}>{t('exampleCalc2')}</li>
        </ul>
      </section>

      {/* Insights Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <h3 style={styles.h3}>{t('insightSimpleVsCompoundTitle')}</h3>
        <p style={styles.paragraph}>{t('insightSimpleVsCompound')}</p>

        <h3 style={styles.h3}>{t('insightTimePeriodTitle')}</h3>
        <p style={styles.paragraph}>{t('insightTimePeriod')}</p>

        <h3 style={styles.h3}>{t('insightCommonUsesTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightUse1')}</li>
          <li style={styles.listItem}>{t('insightUse2')}</li>
          <li style={styles.listItem}>{t('insightUse3')}</li>
          <li style={styles.listItem}>{t('insightUse4')}</li>
        </ul>

        <h3 style={styles.h3}>{t('insightBestPracticesTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightPractice1')}</li>
          <li style={styles.listItem}>{t('insightPractice2')}</li>
          <li style={styles.listItem}>{t('insightPractice3')}</li>
          <li style={styles.listItem}>{t('insightPractice4')}</li>
        </ul>
      </section>

      {/* FAQ Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={styles.refList}>
          <li style={styles.refItem}>
            <a
              href="https://www.nerdwallet.com/banking/learn/simple-interest-calculator-i-prt"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t('ref1')}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.calculator.net/simple-interest-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t('ref2')}
            </a>
          </li>
          <li style={styles.refItem}>
            <a
              href="https://www.businessinsider.com/personal-finance/banking/simple-interest"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.refLink}
            >
              {t('ref3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}